import { useEffect, useState, useMemo, useCallback } from 'react';
import { getTemps, getCalculsTMP, recalculerTousLesTMP, getConsultants } from '../../services/airtable';
import { useYear } from '../../contexts/YearContext';
import { getAvailableYearsFromTemps } from '../../utils/yearUtils';

/**
 * Hook de chargement des données TMP.
 * Encapsule le fetch Airtable, les états de chargement/erreur,
 * et les données dérivées (consultants, tempsByYear, etc.).
 */
export function useTMPData() {
  const { selectedYear: yearFilter, setAvailableYears } = useYear();

  const [temps, setTemps] = useState([]);
  const [calculTMP, setCalculTMP] = useState([]);
  const [consultantsList, setConsultantsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [recalculLoading, setRecalculLoading] = useState(false);
  const [recalculResult, setRecalculResult] = useState(null);
  const [showRecalculConfirm, setShowRecalculConfirm] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [tempsData, calcData, consultantsData] = await Promise.all([
        getTemps(),
        getCalculsTMP(),
        getConsultants()
      ]);
      setTemps(Array.isArray(tempsData) ? tempsData : []);
      setCalculTMP(Array.isArray(calcData) ? calcData : []);
      setConsultantsList(Array.isArray(consultantsData) ? consultantsData : []);

      setAvailableYears(getAvailableYearsFromTemps(tempsData));
      setError(null);
    } catch (err) {
      console.error('Erreur chargement TMP page:', err);
      setError('Erreur lors du chargement des données Airtable.');
    } finally {
      setLoading(false);
    }
  }, [setAvailableYears]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Recharger après reconnexion (session expirée → renouvelée)
  useEffect(() => {
    const handler = () => {
      setError(null);
      loadData();
    };
    window.addEventListener('session:renewed', handler);
    return () => window.removeEventListener('session:renewed', handler);
  }, [loadData]);

  const handleRecalcul = useCallback(() => {
    if (recalculLoading) return;
    setShowRecalculConfirm(true);
  }, [recalculLoading]);

  const confirmRecalcul = useCallback(async () => {
    setShowRecalculConfirm(false);
    setRecalculLoading(true);
    setRecalculResult(null);

    try {
      const result = await recalculerTousLesTMP();
      setRecalculResult(result);
      await loadData();
    } catch (err) {
      setError('Erreur lors du recalcul : ' + err.message);
    } finally {
      setRecalculLoading(false);
    }
  }, [loadData]);

  // Liste des consultants uniques
  const consultants = useMemo(() => {
    const names = new Set();
    (temps || []).forEach(r => {
      const nom = r.consultantNom;
      if (nom && nom !== '-') names.add(nom);
    });
    return Array.from(names).sort();
  }, [temps]);

  // Map consultantNom -> consultantId
  const consultantIdMap = useMemo(() => {
    const map = {};
    (consultantsList || []).forEach(c => {
      map[c.fields?.Nom] = c.id;
    });
    return map;
  }, [consultantsList]);

  // Temps filtrés par année
  const tempsByYear = useMemo(() => {
    if (!yearFilter) return [];
    return (temps || []).filter(r => r.fields?.Année === yearFilter);
  }, [temps, yearFilter]);

  return {
    temps,
    calculTMP,
    consultantsList,
    loading,
    error,
    setError,
    recalculLoading,
    recalculResult,
    setRecalculResult,
    showRecalculConfirm,
    setShowRecalculConfirm,
    loadData,
    handleRecalcul,
    confirmRecalcul,
    consultants,
    consultantIdMap,
    tempsByYear
  };
}
